import { Select as SelectPrimitive } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from '../cn';
import { CheckIcon, ChevronDownIcon } from '../icons';
import { fieldClassName } from './input';

export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface SelectProps extends Omit<ComponentPropsWithoutRef<typeof SelectPrimitive.Root>, 'children'> {
  options: readonly SelectOption[];
  placeholder?: string;
  id?: string;
  className?: string;
  size?: 'sm' | 'md';
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-label'?: string;
}

/** Single-choice select for short, fixed option lists. Use Combobox when the list is long or searchable. */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  { options, placeholder, id, className, size = 'md', ...props },
  ref,
) {
  const { 'aria-invalid': invalid, 'aria-describedby': describedBy, 'aria-label': ariaLabel, ...rootProps } = props;
  return (
    <SelectPrimitive.Root {...rootProps}>
      <SelectPrimitive.Trigger
        ref={ref}
        id={id}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        className={cn(
          fieldClassName,
          size === 'sm' ? 'h-7 text-label' : 'h-8',
          'flex items-center justify-between gap-2 px-2.5 text-start data-[placeholder]:text-ink-muted',
          className,
        )}
      >
        <span className="truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon>
          <ChevronDownIcon size={14} className="text-ink-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-(--z-popover) max-h-80 min-w-(--radix-select-trigger-width) overflow-hidden rounded-md border border-border bg-surface shadow-overlay data-[state=open]:animate-fade-in"
        >
          <SelectPrimitive.Viewport className="overscroll-contain p-1">
            {options.map((o) => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex h-8 items-center rounded-sm ps-7 pe-2 text-body text-ink outline-none select-none data-[disabled]:opacity-50 data-[highlighted]:bg-surface-muted"
              >
                <span className="absolute start-2 flex items-center">
                  <SelectPrimitive.ItemIndicator>
                    <CheckIcon size={14} className="text-accent" />
                  </SelectPrimitive.ItemIndicator>
                </span>
                <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
});
